import { SellInvoiceModelType } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { RenderSellInvoiceStatus } from '../../../app/erp/screens/Logistic/QuoteParent/subcomponents'
import { DisplayPriceGettingParams } from '../../../baseComponents/Displays/DisplayPrice'
import { TwinTableLink } from '../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, CustomRenderDate, EditRow } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { addDictionaryExtraLine, TwinDictionary } from '../../../utils/globals/dictionary'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { OnRowClick } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import locations from '../../../utils/globals/locations'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../utils/reducers'
import { SellTagsPayload } from '../../../utils/reducers/sells/sellTags'
import { RenderSellTags } from '../CustomRenderTable'
import { sortEmployeesByUser } from '../../../utils/globals/employees'
import { useMemo } from 'react'
import { getConfigParam, getEmployees } from '../../../utils/reducers/getters'
import { EmployeesPayload } from '../../../utils/reducers/company/employees'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'

type TableSellInvoicesProps = TableComponentProps & ReduxTableSellInvoices & {
    onRowClick?: OnRowClick
}

const TableSellInvoices: React.FC<TableSellInvoicesProps> = ({ setTableInstance, onRowClick, sellTags }) => {
    const { t } = useTwinTranslation()
    const sellInvoiceStatusComplex: TwinDictionary = {
        0: { name: t('draft', 'Borrador'), id: 0 },
        2: { name: t('invoiced', 'Facturado'), id: 2 },
        4: { name: t('refundedF', 'Devuelta'), id: 4 },
    }
    const memoEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getEmployees() || {}))), [])
    const fieldMemo = useMemo(() => {
        const codeCustomerToShow = getConfigParam('codeCustomerToShow')
        let fieldId: 'id' | 'code' = 'id'
        if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
            fieldId = 'code'
        }
        return fieldId
    }, [])
    const columns: ColumnTableSchema<SellInvoiceModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('invoice', 'Factura'),
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('invoice', 'Factura') }
            },
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={(allRowData?.Customer?.[fieldMemo] ? (allRowData?.Customer?.[fieldMemo] + ' - ') : '') + (allRowData?.Customer?.fiscal_name || allRowData?.Customer?.name || '')} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
        },
        {
            hideColumnByDefault: true,
            justForFilter: true,
            id: 'code',
            dataKey: 'CustomerId',
            label: '',
            searchKey: 'Customer.code',
            searchableComponent: {
                component: 'SearchableComponentBasic',
            },
        },
        {
            id: 'CustomerFiscalNameId',
            dataKey: 'CustomerId',
            label: '',
            hideColumnByDefault: true,
            searchKey: 'Customer.fiscal_name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: '' }
            },
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <RenderSellInvoiceStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: sellInvoiceStatusComplex, label: t('statusInvoice', 'Estado de la factura') }

            },
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            searchKey: 'WorkingCenter.name',
            label: t('soldBy', 'Vendido por'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 120
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customName', 'Nombre personalizado'), }
            },
            hideColumnByDefault: true
        },
        {
            id: 'total_without_vat',
            dataKey: 'total_without_vat',
            label: t('totalWithoutVatS', 'B. imponible'),
            simpleLabel: t('totalWithoutVat', 'Base imponible'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CustomerProjectId',
            dataKey: 'CustomerProjectId',
            label: t('project', 'Proyecto'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.name || ''} />
        },
        {
            id: 'RepresentativeEmployeeId',
            dataKey: 'RepresentativeEmployeeId',
            label: t('representative', 'Representante'),
            searchKey: 'RepresentativeEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.RepresentativeEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('representative', 'Representante'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            searchSingleKey: 'RepresentativeEmployee.id'
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('madeBy', 'Realizado por'),
            hideColumnByDefault: true,
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('madeBy', 'Realizado por'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            searchSingleKey: 'CreateEmployee.id'
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'id',
            hideColumnByDefault: true,
            label: t('projectType', 'Tipo de proyecto'),
            searchKey: 'CustomerProject.CustomerProjectType.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('projectType', 'Tipo de proyecto') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.CustomerProjectType?.name} />
        },
        {
            id: 'SellTags',
            dataKey: 'OrderTags',
            label: t('tags', 'Etiquetas'),
            customRender: (_parameterValue, allRowData) => <RenderSellTags {...{ allRowData, sellTags }} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: sellTags?.sell || {}, label: t('tags', 'Etiquetas') }
            },
            generalSearchable: false,
            sortable: false,
            hideColumnByDefault: true
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'maxRefundDate',
            dataKey: 'maxRefundDate',
            label: t('maxRefundDateShort', 'Fecha máx. dev.'),
            simpleLabel: t('maxRefundDate', 'Fecha máxima de devolución'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || ''} />,
            sortable: true,
            minWidth: 165,
            hideColumnByDefault: true
        },
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.sellInvoiceSingle, ['$id']]} name='sellInvoices' columns={columns} getDataFrom='/api/app/order/sell/sellInvoice/getAllSellInvoicesComplete' extraWhereParams={{
            'is_last': true
        }} onRowClick={onRowClick} setTableInstance={setTableInstance} />
    )
}

const TableSellInvoicesDispatch = {
    setTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxTableSellInvoices = ConnectedProps<typeof tableSellInvoicesConnect>
const mapTableSellInvoicesConnector = (state: AllReduxPayloads) => ({ sellTags: state.sellTags, employees: state.employees })
const tableSellInvoicesConnect = connect(mapTableSellInvoicesConnector, TableSellInvoicesDispatch)
const TableSellInvoicesConnectLoading = withLoading(TableSellInvoices, [{ fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setTags' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])
const TableSellInvoicesConnect = tableSellInvoicesConnect(TableSellInvoicesConnectLoading)

export default TableSellInvoicesConnect